import { UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Input, Checkbox } from 'antd';
import {
  Button,
  Modal,
  Upload,
  message,
  notification,
  Spin,
  Select,
} from 'antd';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { URL_ASYNC_TASK_REPORT } from '../../../constants/URLS';
import { useSAUsers } from '../../../custom-hooks/sa-users';
import { fetchAPI } from '../../../utils/api';

const { Option } = Select;

const REGION_OPTIONS = [
  { value: 'CN', label: 'China (CN)' },
  { value: 'JP', label: 'Japan (JP)' },
  { value: 'SG', label: 'Singapore (SG)' },
  { value: 'AU', label: 'Australia (AU)' },
  { value: 'HK', label: 'Hong Kong (HK)' },
  { value: 'KR', label: 'South Korea (KR)' },
  { value: 'TW', label: 'Taiwan (TW)' },
  { value: 'MY', label: 'Malaysia (MY)' },
  { value: 'TH', label: 'Thailand (TH)' },
  { value: 'IN', label: 'India (IN)' },
  { value: 'ID', label: 'Indonesia (ID)' },
  { value: 'PH', label: 'Philippines (PH)' },
  { value: 'VN', label: 'Vietnam (VN)' },
  { value: 'NZ', label: 'New Zealand (NZ)' },
  { value: 'US', label: 'USA (US)' },
];

const UploadSpidReport = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('CN');
  const [fileUrlInput, setFileUrlInput] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(
    undefined
  );
  const [isDebug, setIsDebug] = useState(false);
  const users = useSAUsers();
  const userList = users?.data?.list || [];
  const sortedUsers = useMemo(() => {
    const sorted = [...userList];
    sorted.sort((a, b) =>
      a.full_name < b.full_name ? -1 : a.full_name > b.full_name ? 1 : 0
    );
    return sorted;
  }, [userList]);

  const showModal = () => {
    setIsModalVisible(true);
    setUploadSuccess(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleChange = async (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }

    if (info.file.status === 'done') {
      // Keep uploading true while we call the second API
      const fileUrl = info.file.response?.url;

      if (fileUrl) {
        try {
          // 2. Generate SPID
          const res = await fetchAPI('reports/async/generate/spid', {
            method: 'POST',
            version: '1.0',
            payload: {
              file_url: fileUrl,
              region: selectedRegion,
              ...(isDebug && { is_debug: true }),
              ...(selectedUserId && { request_by_id: selectedUserId }),
            },
          });

          setUploadSuccess(true);
          setUploading(false);

          if (isDebug) {
            const data = res?.ok || res;
            const errors = data.errors || [];
            notification.info({
              message: 'Debug Mode Results',
              duration: 0,
              description: (
                <div>
                  <p>
                    <strong>Processed:</strong> {data.processed ?? '?'}
                  </p>
                  <p>
                    <strong>Failed:</strong> {data.failed ?? '?'}
                  </p>
                  <p>
                    <strong>Skipped:</strong> {data.skipped ?? '?'}
                  </p>
                  {errors.length > 0 && (
                    <p>
                      <strong>Errors:</strong> {errors.length}
                    </p>
                  )}
                  <p style={{ color: '#888', marginTop: 8 }}>
                    No changes saved (debug mode).
                  </p>
                </div>
              ),
            });
          } else {
            notification.success({
              message: 'Upload Successful',
              description: 'SPID generation started.',
            });
          }
        } catch (error) {
          console.error(error);
          message.error('Failed to trigger SPID generation');
          setUploading(false);
        }
      } else {
        message.error('File upload failed: No URL returned');
        setUploading(false);
      }
    } else if (info.file.status === 'error') {
      setUploading(false);
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const handleUrlSubmit = async () => {
    if (!fileUrlInput) return;
    setUploading(true);
    try {
      const res = await fetchAPI('reports/async/generate/spid', {
        method: 'POST',
        version: '1.0',
        payload: {
          file_url: fileUrlInput,
          region: selectedRegion,
          ...(isDebug && { is_debug: true }),
          ...(selectedUserId && { request_by_id: selectedUserId }),
        },
      });

      setUploadSuccess(true);
      setUploading(false);

      if (isDebug) {
        const data = res?.ok || res;
        const errors = data.errors || [];
        notification.info({
          message: 'Debug Mode Results',
          duration: 0,
          description: (
            <div>
              <p>
                <strong>Processed:</strong> {data.processed ?? '?'}
              </p>
              <p>
                <strong>Failed:</strong> {data.failed ?? '?'}
              </p>
              <p>
                <strong>Skipped:</strong> {data.skipped ?? '?'}
              </p>
              {errors.length > 0 && (
                <p>
                  <strong>Errors:</strong> {errors.length}
                </p>
              )}
              <p style={{ color: '#888', marginTop: 8 }}>
                No changes saved (debug mode).
              </p>
            </div>
          ),
        });
      } else {
        notification.success({
          message: 'Processing Started',
          description: 'SPID generation started.',
        });
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to trigger SPID generation');
      setUploading(false);
    }
  };

  return (
    <>
      <Button icon={<UploadOutlined />} onClick={showModal}>
        Upload SPID Report
      </Button>
      <Modal
        title="Upload SPID Report"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={
          uploadSuccess ? <Button onClick={handleCancel}>Close</Button> : null
        }
        closable={!uploading}
        maskClosable={!uploading}
      >
        {uploadSuccess ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircleOutlined
              style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }}
            />
            <h3>Upload & Processing Started!</h3>
            <p>
              The file has been uploaded and SPID generation is running in the
              background.
            </p>
            <p>
              You can check the progress at the{' '}
              <Link to={URL_ASYNC_TASK_REPORT} onClick={handleCancel}>
                Async Task Report
              </Link>{' '}
              page.
            </p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ marginRight: 8 }}>Region:</label>
              <Select
                value={selectedRegion}
                onChange={value => setSelectedRegion(value as string)}
                style={{ width: 200 }}
                disabled={uploading}
              >
                {REGION_OPTIONS.map(opt => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ marginRight: 8 }}>User (responsible):</label>
              <Select
                value={selectedUserId}
                onChange={value => setSelectedUserId(value)}
                style={{ width: 200 }}
                placeholder="Select user (default: current user)"
                allowClear
                showSearch
                optionFilterProp="children"
                disabled={uploading}
              >
                {sortedUsers.map(u => (
                  <Option key={u.id} value={u.id}>
                    {u.full_name}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Checkbox
                checked={isDebug}
                onChange={e => setIsDebug(e.target.checked)}
                disabled={uploading}
              >
                Debug mode (no DB changes)
              </Checkbox>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Input
                placeholder="Or paste file URL directly..."
                value={fileUrlInput}
                onChange={e => setFileUrlInput(e.target.value)}
                disabled={uploading}
              />
            </div>

            {fileUrlInput ? (
              <Button
                type="primary"
                onClick={handleUrlSubmit}
                loading={uploading}
                block
              >
                Process URL
              </Button>
            ) : (
              <Upload.Dragger
                name="file"
                multiple={false}
                action="https://seller.sourcesage.co/dbs/api/v1.0/upload"
                data={{ type: 'spid_report' }}
                onChange={handleChange}
                showUploadList={false}
                disabled={uploading}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for a single upload. Strictly prohibit from uploading
                  company data or other band files
                </p>
              </Upload.Dragger>
            )}

            {uploading && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Spin tip="Uploading and processing..." />
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default UploadSpidReport;
